import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useUpdateCryptoOnchainPayoutAddresses,
  type CryptoOnchainStoreStatus,
  type CryptoOnchainTestnetAddressKind,
} from '@/lib/api/crypto-onchain';

/**
 * Test networks with a wallet of their own: a Bitcoin or Litecoin testnet address is written
 * differently from a mainnet one. Mirrors `CRYPTO_ONCHAIN_SANDBOX_TESTNET_ADDRESS_KINDS`
 * (src/lib/schemas/cryptoOnchain.ts); Dogecoin waits.
 */
const TESTNET_KINDS = ['btc', 'ltc'] as const satisfies readonly CryptoOnchainTestnetAddressKind[];
type TestnetKind = (typeof TESTNET_KINDS)[number];

/** Address prefixes, not copy: they tell the merchant which wallet goes where. */
const PLACEHOLDERS: Record<TestnetKind, string> = { btc: 'tb1…', ltc: 'tltc1…' };

type Props = {
  storeId: string;
  status: CryptoOnchainStoreStatus;
};

/**
 * The Bitcoin Signet and Litecoin Testnet wallets of a store in sandbox mode, saved on their own
 * (only the ones that changed, an emptied one is cleared). EVM, Solana and Tron wallets need none:
 * they work on their test networks as they are. crypto-api checks the testnet encoding.
 */
export function CryptoOnchainTestnetAddressesCard({ storeId, status }: Props) {
  const { t } = useTranslation('integrations');
  const update = useUpdateCryptoOnchainPayoutAddresses();
  const saved = status.testnetPayoutAddresses ?? {};
  const [values, setValues] = useState<Record<TestnetKind, string>>({
    btc: saved.btc ?? '',
    ltc: saved.ltc ?? '',
  });
  const changed = Object.fromEntries(
    TESTNET_KINDS.map((kind) => [kind, values[kind].trim()] as const).filter(
      ([kind, value]) => value !== (saved[kind] ?? ''),
    ),
  );
  const hasSpaces = TESTNET_KINDS.some((kind) => /\s/.test(values[kind].trim()));
  const isSaveDisabled = update.isPending || hasSpaces || Object.keys(changed).length === 0;

  const handleSave = async () => {
    try {
      await update.mutateAsync({ storeId, payoutAddresses: {}, testnetPayoutAddresses: changed });
    } catch {
      // The mutation hook already showed the translated toast; the fields keep what was typed.
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('cryptoOnchain.testnetAddresses.title')}</CardTitle>
        <CardDescription>{t('cryptoOnchain.testnetAddresses.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {TESTNET_KINDS.map((kind) => (
          <div key={kind} className="space-y-2">
            <Label htmlFor={`crypto-onchain-testnet-${kind}`}>{t(`cryptoOnchain.testnetAddresses.${kind}`)}</Label>
            <Input
              id={`crypto-onchain-testnet-${kind}`}
              placeholder={PLACEHOLDERS[kind]}
              value={values[kind]}
              disabled={update.isPending}
              autoComplete="off"
              spellCheck={false}
              className="font-mono text-sm"
              onChange={(event) => setValues((current) => ({ ...current, [kind]: event.target.value }))}
            />
          </div>
        ))}
        {hasSpaces && <p className="text-destructive text-xs">{t('cryptoOnchain.testnetAddresses.noSpaces')}</p>}
        <Button type="button" variant="outline" disabled={isSaveDisabled} onClick={handleSave}>
          {t('cryptoOnchain.testnetAddresses.save')}
        </Button>
      </CardContent>
    </Card>
  );
}
